// 牌的視覺元件
// 對應 PRD §3.2.3 UI/Scenes、§16.3 預留接口（多套牌面皮膚）
//
// === 使用圖片（assets，於 CBootScene 載入） ===
// - public/assets/tiles/tile-blank-front.png（key: 'tile-blank-front'，90×144）— 米色牌底，正面 face 的下層
// - public/assets/tiles/tiles-source.png（key: TILE_SHEET_KEY = 'tile-sheet'，42 frames，每張 63×77）
//     所有 face 的「牌面圖案」layer：0–8 萬、9–17 筒、18–26 索、27–30 風、31–33 元、34–41 花
// - public/assets/tiles/tile-back-up.png（key: 'tile-back-up'，63×99）→ hand-top（上家手牌，直立牌背）
// - public/assets/tiles/tile-back-down.png（key: 'tile-back-down'，63×99）— 對手暗槓牌背
// - public/assets/tiles/tile-side-up.png（key: 'tile-side-up'，63×99）
//     → hand-left（左家手牌，直接用）
//     → hand-right（右家手牌，setFlipX(true) 左右翻轉）
// - public/assets/tiles/tile-side-down.png（key: 'tile-side-down'，90×63）→ 左右家副露 (meld-left / meld-right)
//     橫躺牌底 + 上層疊 pattern（旋轉 ∓90° 對齊橫躺方向）
// - public/assets/tiles/tile-front-down.png（key: 'tile-front-down'，63×99）→ discard 一般捨牌底（藍邊）
// - 接觸陰影：無圖檔 — 執行期以 Canvas 2D 繪製的 CanvasTexture（key: TILE_SHADOW_KEY），所有牌共用
//
// === 牌面合成原則 ===
// - 「正面」一律由兩層 image 疊起：
//     下層：tile-blank-front（米色牌底 + 藍邊外框）
//     上層：tiles-source 對應 frame（黑紅字 / 圖樣，背景透明）
//   這樣 spritesheet 圖案保持不變，換主題只要換 blank-front 與 sheet。
// - 「背面」單張 image：tile-back-up（直立牌背）或 tile-back-down（倒立牌背）
//
// 9 種 face 涵蓋整局視覺：4 家 (下/上/左/右) × 2 狀態 (手牌/副露) + 海底 = 9
// 視覺差異透過「正/背 + 旋轉角度」組合達成；座位排版方向交給 CHandView/CDiscardPool。

import { tileToIndex, type TTile } from '@core/tile';
import { CARD_FRAME_KEY, TILE_SHEET_KEY } from '@scenes/boot-scene';
import Phaser from 'phaser';

// ============================================================================
// 版面常數（要微調 sprite 視覺都改這裡）
// ============================================================================

// 牌底寬高定義移到 Phaser-free 的 tile-geometry.ts（供 hand-layout 純佈局共用）；此處 re-export 維持相容。
export { TILE_BODY_WIDTH, TILE_BODY_HEIGHT } from './tile-geometry';

// ---- pattern 在 Container 局部座標中相對底圖中心的 Y 偏移（補償視覺面中心 ≠ 幾何中心） ----
// 微調原則：以 face 的「視覺重心」為準，藍色邊條較厚的一側留多一點空白
const PATTERN_OFFSET_FRONT_Y = 14; // tile-blank-front 頂部 ridge 略厚 → pattern 微下
const PATTERN_OFFSET_SIDE_Y = -14; // tile-side-down  底部 ridge 略厚 → pattern 微上
const PATTERN_OFFSET_DISCARD_Y = -6; // card-front-down 頂部 ridge 略厚 → pattern 微下

// ---- 其他 sprite 視覺常數 ----
const HIDDEN_FRONT_WIDTH = 90;
const HIDDEN_FRONT_HEIGHT = 144;
const HIDDEN_SIDE_WIDTH = 90;
const HIDDEN_SIDE_HEIGHT = 63;
const HIDDEN_FLAT_WIDTH = 63;
const HIDDEN_FLAT_HEIGHT = 99;
const FRONT_PATTERN_SCALE = 1.25; // 'front' kind pattern 放大倍率（讓圖案填滿牌面而非空蕩蕩）
const DISCARD_PATTERN_SCALE = 0.85; // 'discard-front' kind pattern 縮放（小牌底較小，pattern 跟著縮以免溢出）

// ---- highlight 邊框（玩家手牌可選 / 放大預覽用） ----
// 用 card-frame.png（81×117 黃色圓角框）取代之前的純 Graphics 邊框
// 預設 scale 1.35，讓框略大於圖案中心、視覺包住牌面；可依美術 feedback 調整
const HIGHLIGHT_FRAME_SCALE = 1.35;

// ---- 接觸陰影（牌身右下方的柔邊陰影，讓牌看起來立在桌面上） ----
// 陰影 texture 執行期以 Canvas 2D shadowBlur 畫一次（key: TILE_SHADOW_KEY），所有牌共用並依牌身尺寸拉伸
export const TILE_SHADOW_KEY = 'tile-shadow'; // 執行期生成的陰影 CanvasTexture key
const SHADOW_OFFSET_X = 3; // 陰影相對牌身往右偏移（螢幕座標，不隨牌旋轉）
const SHADOW_OFFSET_Y = 5; // 陰影相對牌身往下偏移（螢幕座標，不隨牌旋轉）
const SHADOW_ALPHA = 0.45; // 陰影整體透明度
const SHADOW_TEX_BODY = 100; // 陰影 texture 內牌身邊長（拉伸到實際牌身尺寸）
const SHADOW_TEX_PAD = 16; // 陰影 texture 四周留給模糊擴散的邊距
const SHADOW_TEX_RADIUS = 12; // 陰影牌身圓角
const SHADOW_TEX_BLUR = 8; // Canvas shadowBlur 模糊程度
const SHADOW_TEX_FAR = 10000; // 牌身畫在畫布外、只把陰影偏移回畫布內的距離

/**
 * 牌的呈現方向（共 9 種，涵蓋全部牌局視覺情境）。
 *
 * 命名規則：`<狀態>-<座位>`
 * - 狀態：`hand`（手牌，暗藏）、`meld`（副露，公開）、`discard`（海底捨牌）
 * - 座位：`bottom`（下家／自家）、`top`（上家／對家）、`left`（左家）、`right`（右家）
 *
 * 視覺對照（base 欄位列出實際使用的 asset 檔名）：
 * | face            | base asset                                  | rotation / flip       | 用途                       |
 * | --------------- | ------------------------------------------- | --------------------- | -------------------------- |
 * | hand-bottom     | tile-blank-front.png + tiles-source.png     |   0°                  | 自家手牌（正面朝玩家）     |
 * | hand-top        | tile-back-up.png                            |   0°                  | 上家手牌（直立牌背）       |
 * | hand-left       | tile-side-up.png                            |   0°                  | 左家手牌（直立側面）       |
 * | hand-right      | tile-side-up.png                            |   0°、flipX           | 右家手牌（直立側面，鏡像） |
 * | meld-bottom     | tile-front-down.png + tiles-source.png      |   0°                  | 自家副露（小牌底，朝玩家） |
 * | meld-top        | tile-blank-front.png + tiles-source.png     | 180°                  | 上家副露（正面倒立）       |
 * | meld-left       | tile-side-down.png + tiles-source.png       |   0°，pattern -90°    | 左家副露（橫躺牌底）       |
 * | meld-right      | tile-side-down.png + tiles-source.png       |   0°，pattern  90°    | 右家副露（橫躺牌底）       |
 * | discard         | tile-front-down.png + tiles-source.png      |   0°                  | 海底捨牌（正面朝玩家）     |
 *
 * value 是字串字面值（直接寫入 sprite 渲染），以 `as const` 鎖定字面值型別並推導 TTileFace 聯集。
 * 程式中引用一律填 const（`TileFace.HandBottom`），不寫字串字面值。
 */
export const TileFace = {
  HandBottom: 'hand-bottom',
  HandTop: 'hand-top',
  HandLeft: 'hand-left',
  HandRight: 'hand-right',
  MeldBottom: 'meld-bottom',
  MeldTop: 'meld-top',
  MeldLeft: 'meld-left',
  MeldRight: 'meld-right',
  Discard: 'discard',
} as const;

export type TTileFace = (typeof TileFace)[keyof typeof TileFace];

/** 牌面 sprite 的主題參數（PRD §16.3 預留）。 */
export type TTileTheme = {
  /** spritesheet key（圖案集，背景透明） */
  readonly sheetKey: string;
  /** 牌底 key（米色牌身） */
  readonly blankFrontKey: string;
  /** 牌背 key */
  readonly backUpKey: string;
  readonly backDownKey: string;
  readonly sideUpKey: string;
  readonly sideDownKey: string;
  /** 海底捨牌牌底（一般，藍邊） */
  readonly discardFrontKey: string;
};

export const DEFAULT_TILE_THEME: TTileTheme = {
  sheetKey: TILE_SHEET_KEY,
  blankFrontKey: 'tile-blank-front',
  backUpKey: 'tile-back-up',
  backDownKey: 'tile-back-down',
  sideUpKey: 'tile-side-up',
  sideDownKey: 'tile-side-down',
  discardFrontKey: 'tile-front-down',
};

/**
 * 把 TTile 對應到 spritesheet 的 frame index。
 *
 * frame 排版（CBootScene 設定）：
 *   0–8   man 1-9
 *   9–17  pin 1-9
 *   18–26 sou 1-9
 *   27–30 wind 東南西北
 *   31–33 dragon 中發白
 *   34–41 flower 春夏秋冬梅蘭菊竹
 */
export const tileToFrameIndex = (tile: TTile): number => tileToIndex(tile);

/**
 * 生成（或沿用已存在的）牌的接觸陰影 texture；整個 game 只畫一次。
 *
 * @returns texture 是否可用（單元測試的假 scene 沒有 TextureManager 時回 false，略過陰影）
 */
const ensureTileShadowTexture = (scene: Phaser.Scene): boolean => {
  const textures = scene.textures as Phaser.Textures.TextureManager | undefined;
  if (!textures) return false;
  if (textures.exists(TILE_SHADOW_KEY)) return true;
  const size = SHADOW_TEX_BODY + SHADOW_TEX_PAD * 2;
  const texture = textures.createCanvas(TILE_SHADOW_KEY, size, size);
  const ctx = texture?.getContext();
  if (!texture || !ctx) return false;
  ctx.shadowColor = '#000';
  ctx.shadowBlur = SHADOW_TEX_BLUR;
  ctx.shadowOffsetX = SHADOW_TEX_FAR;
  ctx.beginPath();
  ctx.roundRect(
    SHADOW_TEX_PAD - SHADOW_TEX_FAR,
    SHADOW_TEX_PAD,
    SHADOW_TEX_BODY,
    SHADOW_TEX_BODY,
    SHADOW_TEX_RADIUS
  );
  ctx.fill();
  texture.refresh();
  return true;
};

// ----------------------------------------------------------------------
// face → 視覺參數對照表
// ----------------------------------------------------------------------
//
// 四種渲染模式：
// - 'front'         : 兩層 → tile-blank-front + pattern（正立／倒立正面 face）
// - 'back'          : 單層 → 牌背 image（hand-top / hand-left / hand-right）
// - 'side-front'    : 兩層 → tile-side-down 橫躺牌底 + pattern（pattern 另外旋轉以對齊橫躺方向）
// - 'discard-front' : 兩層 → card-front-down(frame 0) + pattern（海底捨牌專用底圖）
//
// rotationDeg 套在 Container 上整體旋轉；patternRotationDeg 只套在 side-front 的 pattern 子物件。
// flipX 直接套在 base image 上做水平鏡像（hand-right 用 hand-left 的圖鏡像而成）。
//
// patternOffsetY：補償底圖視覺面中心 ≠ 幾何中心 的偏移（藍色邊條使 face 偏向一側）。
// - tile-blank-front：頂部藍色 ridge 較厚 → pattern 往下 +8
// - tile-side-down  ：底部藍色 ridge 較厚 → pattern 往上 -8（鏡向 tile-blank-front）
// - card-front-down ：邊框近似對稱 → 不需偏移（0）

type TBaseImageKey = 'backUp' | 'backDown' | 'sideUp' | 'sideDown';

type TFaceRenderSpec = {
  readonly kind: 'front' | 'back' | 'side-front' | 'discard-front';
  /** Container 整體旋轉（degree，順時針為正） */
  readonly rotationDeg: number;
  /** 底圖左右翻轉（hand-right 用） */
  readonly flipX?: boolean;
  /** 'back' / 'side-front' 用的 base image key */
  readonly baseImage?: TBaseImageKey;
  /** 'side-front' 時 pattern 子物件的額外旋轉角度（degree） */
  readonly patternRotationDeg?: number;
};

const FACE_SPECS: Record<TTileFace, TFaceRenderSpec> = {
  [TileFace.HandBottom]: { kind: 'front', rotationDeg: 0 },
  [TileFace.HandTop]: { kind: 'back', rotationDeg: 0, baseImage: 'backUp' },
  [TileFace.HandLeft]: { kind: 'back', rotationDeg: 0, baseImage: 'sideUp' },
  [TileFace.HandRight]: {
    kind: 'back',
    rotationDeg: 0,
    baseImage: 'sideUp',
    flipX: true,
  },
  // 自家副露：用 tile-front-down 小牌底（與海底捨牌同款，但語意是副露）
  [TileFace.MeldBottom]: { kind: 'discard-front', rotationDeg: 0 },
  [TileFace.MeldTop]: { kind: 'front', rotationDeg: 180 },
  [TileFace.MeldLeft]: {
    kind: 'side-front',
    rotationDeg: 0,
    baseImage: 'sideDown',
    patternRotationDeg: 90, // 左家副露：原 -90° 再轉 180° = 90°（圖案朝向對家視角的「正立」）
  },
  [TileFace.MeldRight]: {
    kind: 'side-front',
    rotationDeg: 0,
    baseImage: 'sideDown',
    patternRotationDeg: -90, // 右家副露：原 90° 再轉 180° = -90°（圖案朝向對家視角的「正立」）
  },
  [TileFace.Discard]: { kind: 'discard-front', rotationDeg: 0 },
};

/**
 * CTileSprite — 一張牌的 Container（face layers + 可選的高亮邊框）。
 *
 * Container 本身沒有大小，內部子物件決定。子物件以 Container 中心 (0,0) 為基準。
 */
export class CTileSprite extends Phaser.GameObjects.Container {
  private faceLayers: Phaser.GameObjects.Image[] = [];
  private shadowLayer: Phaser.GameObjects.Image | null = null;
  private highlightBorder: Phaser.GameObjects.Image | null = null;
  private currentFace: TTileFace;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    public readonly tile: TTile,
    face: TTileFace = TileFace.HandBottom,
    private readonly theme: TTileTheme = DEFAULT_TILE_THEME,
    private readonly hiddenMeld = false
  ) {
    super(scene, x, y);
    this.currentFace = face;
    this.rebuildFace(face);
    scene.add.existing(this);
  }

  /** 依 face 重建 face layers（清掉舊的、加新的），並套用對應旋轉。 */
  private rebuildFace(face: TTileFace): void {
    for (const l of this.faceLayers) l.destroy();
    this.faceLayers.length = 0;

    const spec = FACE_SPECS[face];

    if (this.hiddenMeld) {
      const back = this.scene.add.image(0, 0, this.theme.backDownKey);
      if (spec.kind === 'side-front') {
        back.setDisplaySize(HIDDEN_SIDE_HEIGHT, HIDDEN_SIDE_WIDTH);
        back.setAngle(spec.patternRotationDeg ?? 0);
      } else if (spec.kind === 'front') {
        back.setDisplaySize(HIDDEN_FRONT_WIDTH, HIDDEN_FRONT_HEIGHT);
      } else {
        back.setDisplaySize(HIDDEN_FLAT_WIDTH, HIDDEN_FLAT_HEIGHT);
      }
      this.faceLayers.push(back);
      this.add(back);
    } else if (spec.kind === 'front') {
      // 兩層直立正面：牌底（90×144）+ 圖案 frame（FRONT_PATTERN_SCALE 填滿牌面）
      const blank = this.scene.add.image(0, 0, this.theme.blankFrontKey);
      const frame = tileToFrameIndex(this.tile);
      const pattern = this.scene.add
        .image(0, PATTERN_OFFSET_FRONT_Y, this.theme.sheetKey, frame)
        .setScale(FRONT_PATTERN_SCALE);
      this.faceLayers.push(blank, pattern);
      this.add(blank);
      this.add(pattern);
    } else if (spec.kind === 'back') {
      // 單層背面（hand-top / hand-left / hand-right）
      const img = this.scene.add.image(0, 0, this.resolveBaseKey(spec.baseImage!));
      if (spec.flipX) img.setFlipX(true);
      this.faceLayers.push(img);
      this.add(img);
    } else if (spec.kind === 'side-front') {
      // 橫躺牌底 + 旋轉過的 pattern（meld-left / meld-right）
      const sideBase = this.scene.add.image(0, 0, this.resolveBaseKey(spec.baseImage!));
      if (spec.flipX) sideBase.setFlipX(true);
      const frame = tileToFrameIndex(this.tile);
      const pattern = this.scene.add
        .image(0, PATTERN_OFFSET_SIDE_Y, this.theme.sheetKey, frame)
        .setScale(0.6);
      if (spec.patternRotationDeg !== undefined) {
        pattern.setRotation(Phaser.Math.DegToRad(spec.patternRotationDeg));
      }
      this.faceLayers.push(sideBase, pattern);
      this.add(sideBase);
      this.add(pattern);
    } else {
      // 'discard-front'：海底捨牌專用底圖（tile-front-down）+ pattern
      const cardBase = this.scene.add.image(0, 0, this.theme.discardFrontKey);
      const frame = tileToFrameIndex(this.tile);
      const pattern = this.scene.add
        .image(0, PATTERN_OFFSET_DISCARD_Y, this.theme.sheetKey, frame)
        .setScale(DISCARD_PATTERN_SCALE);
      this.faceLayers.push(cardBase, pattern);
      this.add(cardBase);
      this.add(pattern);
    }

    // Container 整體旋轉（highlight border 也會跟著轉）
    this.setRotation(Phaser.Math.DegToRad(spec.rotationDeg));
    this.rebuildShadow();
  }

  /** 依目前牌身底圖尺寸重建接觸陰影，放在最底層；偏移量反向補償 Container 旋轉，讓陰影固定落在螢幕右下。 */
  private rebuildShadow(): void {
    this.shadowLayer?.destroy();
    this.shadowLayer = null;
    const base = this.faceLayers[0];
    if (!base || !ensureTileShadowTexture(this.scene)) return;

    const quarterTurn = Math.abs(Math.round(base.angle / 90)) % 2 === 1;
    const bodyW = quarterTurn ? base.displayHeight : base.displayWidth;
    const bodyH = quarterTurn ? base.displayWidth : base.displayHeight;
    const stretch = (SHADOW_TEX_BODY + SHADOW_TEX_PAD * 2) / SHADOW_TEX_BODY;
    const cos = Math.cos(-this.rotation);
    const sin = Math.sin(-this.rotation);
    const shadow = this.scene.add
      .image(
        SHADOW_OFFSET_X * cos - SHADOW_OFFSET_Y * sin,
        SHADOW_OFFSET_X * sin + SHADOW_OFFSET_Y * cos,
        TILE_SHADOW_KEY
      )
      .setDisplaySize(bodyW * stretch, bodyH * stretch)
      .setAlpha(SHADOW_ALPHA);
    this.shadowLayer = shadow;
    this.addAt(shadow, 0);
  }

  /** 把 baseImage 名稱對到 theme 內對應的 texture key。 */
  private resolveBaseKey(name: TBaseImageKey): string {
    switch (name) {
      case 'backUp':
        return this.theme.backUpKey;
      case 'backDown':
        return this.theme.backDownKey;
      case 'sideUp':
        return this.theme.sideUpKey;
      case 'sideDown':
        return this.theme.sideDownKey;
    }
  }

  /** 切換牌面方向。 */
  setFace(face: TTileFace): void {
    if (face === this.currentFace) return;
    this.rebuildFace(face);
    this.currentFace = face;
  }

  /**
   * 啟用 / 關閉黃色高亮邊框（card-frame.png 圓角黃框）。
   * 使用情境：
   *   - 玩家可選的手牌（提示「可點」）
   *   - 點選後上方的放大預覽（提示「點此確認」）
   * 框會疊在牌面「之上」（讓黃色邊清楚可見、不被牌底圖案遮）。
   */
  setHighlight(on: boolean): void {
    if (on) {
      if (!this.highlightBorder) {
        this.highlightBorder = this.scene.add
          .image(0, 0, CARD_FRAME_KEY)
          .setScale(HIGHLIGHT_FRAME_SCALE);
        this.add(this.highlightBorder); // 放最上層，邊框疊在牌面之上
      }
      this.highlightBorder.setVisible(true);
    } else if (this.highlightBorder) {
      this.highlightBorder.setVisible(false);
    }
  }
}
